import React, { useState } from "react";
import {
  Building2,
  Check,
  Copy,
  Loader2,
} from "lucide-react";

function CopyRow({
  label,
  value,
  important = false,
  multiline = false,
}) {
  const [copied, setCopied] = useState(false);

  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const displayValue = Array.isArray(value)
    ? value.join(", ")
    : String(value);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(
        displayValue
      );

      setCopied(true);

      window.setTimeout(() => {
        setCopied(false);
      }, 1200);
    } catch {
      // Clipboard support is optional.
    }
  }

  return (
    <div
      className={`rounded-2xl px-4 py-3 ${
        important
          ? "bg-[#FFF7D6]"
          : "bg-[#F7F8F7]"
      }`}
    >
      <p
        className={`text-[9px] font-semibold uppercase tracking-[0.16em] ${
          important
            ? "text-[#0A4730]/45"
            : "text-[#0A2540]/40"
        }`}
      >
        {label}
      </p>

      <div className="mt-1.5 flex items-start justify-between gap-3">
        <p
          className={`min-w-0 flex-1 text-sm font-semibold text-[#0A2540] ${
            multiline
              ? "whitespace-pre-wrap break-words"
              : "break-all"
          }`}
        >
          {displayValue}
        </p>

        <button
          type="button"
          onClick={handleCopy}
          aria-label={`Copy ${label}`}
          title={`Copy ${label}`}
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition active:scale-95 ${
            important
              ? "bg-white text-[#0A4730]"
              : "bg-white text-[#0A4730] shadow-sm"
          }`}
        >
          {copied ? (
            <Check size={14} />
          ) : (
            <Copy size={14} />
          )}
        </button>
      </div>
    </div>
  );
}

function formatAmount(amount, currency) {
  const numeric = Number(amount);

  if (!Number.isFinite(numeric)) {
    return amount
      ? `${currency || ""} ${amount}`.trim()
      : null;
  }

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency || "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return `${currency || ""} ${numeric.toFixed(2)}`.trim();
  }
}

export function BankFundingCard({
  instructions,
  isLoading = false,
  notReady = false,
  error = null,
}) {
  if (
    isLoading ||
    (!instructions && notReady)
  ) {
    return (
      <div className="flex w-full max-w-[430px] items-center gap-3 rounded-3xl border border-[#0A4730]/10 bg-white p-5 shadow-sm">
        <Loader2
          size={18}
          className="shrink-0 animate-spin text-[#0A4730]"
        />

        <div>
          <p className="text-sm font-semibold text-[#0A4730]">
            Getting bank details…
          </p>

          <p className="mt-0.5 text-xs text-[#0A2540]/50">
            This normally takes a moment.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-[430px] rounded-3xl border border-red-100 bg-red-50 p-4">
        <p className="text-sm font-semibold text-red-600">
          Bank details aren't available yet.
        </p>

        <p className="mt-1 text-xs text-red-500/80">
          Please try again shortly.
        </p>
      </div>
    );
  }

  if (!instructions) {
    return null;
  }

  /*
   * Authoritative funding-instructions response:
   *
   * {
   *   amount,
   *   currency,
   *   reference,
   *   instructions: {
   *     bank_name,
   *     account_name,
   *     account_number,
   *     sort_code,
   *     iban,
   *     bic,
   *     ...
   *   }
   * }
   *
   * Keep fallbacks because older/customer-safe responses
   * may use slightly different field names.
   */
  const bank =
    instructions.instructions ||
    instructions.bank_details ||
    instructions.bank ||
    instructions;

  const bankName =
    bank.bank_name ||
    instructions.bank_name;

  const accountName =
    bank.account_name ||
    bank.account_holder_name ||
    bank.beneficiary_name ||
    instructions.account_name ||
    instructions.account_holder_name;

  const iban =
    bank.iban ||
    instructions.iban;

  const accountNumber =
    bank.account_number ||
    bank.bank_account_number ||
    instructions.account_number ||
    instructions.bank_account_number;

  const sortCode =
    bank.sort_code ||
    instructions.sort_code;

  const routingNumber =
    bank.routing_number ||
    bank.bank_routing_number ||
    instructions.routing_number ||
    instructions.bank_routing_number;

  const bicSwift =
    bank.bic ||
    bank.swift ||
    bank.swift_code ||
    instructions.bic ||
    instructions.swift ||
    instructions.swift_code;

  const bankCode =
    bank.bank_code ||
    instructions.bank_code;

  const bankAddress =
    bank.bank_address ||
    instructions.bank_address;

  const beneficiaryAddress =
    bank.beneficiary_address ||
    bank.bank_beneficiary_address ||
    instructions.beneficiary_address ||
    instructions.bank_beneficiary_address;

  const paymentRails =
    bank.payment_rails ||
    bank.payment_rail ||
    instructions.payment_rails ||
    instructions.payment_rail;

  /*
   * Reference intentionally prefers the top-level
   * customer-safe value returned by the endpoint.
   */
  const reference =
    instructions.reference ||
    instructions.required_reference ||
    bank.reference ||
    bank.required_reference;

  const amount = formatAmount(
    instructions.amount,
    instructions.currency
  );

  const hasPrimaryDetails = Boolean(
    bankName ||
      accountName ||
      iban ||
      accountNumber ||
      sortCode ||
      routingNumber ||
      bicSwift
  );

  return (
    <div className="w-full max-w-[430px] overflow-hidden rounded-3xl border border-[#0A4730]/10 bg-white shadow-[0_8px_30px_rgba(10,71,48,0.07)]">
      {/* HEADER */}
      <div className="bg-[#0A4730] px-5 py-4 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10">
            <Building2 size={17} />
          </div>

          <div>
            <p className="text-sm font-semibold">
              Bank transfer details
            </p>

            <p className="mt-0.5 text-[11px] text-white/65">
              Send the exact amount using these details.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {/* AMOUNT */}
        {amount && (
          <div className="rounded-2xl border border-[#0A4730]/10 bg-white px-4 py-3">
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#0A4730]/40">
              Amount to send
            </p>

            <p className="mt-1 text-lg font-bold text-[#0A4730]">
              {amount}
            </p>
          </div>
        )}

        {/* PRIMARY BANK DETAILS */}
        {hasPrimaryDetails && (
          <div className="space-y-2">
            <p className="px-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#0A4730]/35">
              Account details
            </p>

            <CopyRow
              label="Bank name"
              value={bankName}
            />

            <CopyRow
              label="Account name"
              value={accountName}
            />

            <CopyRow
              label="IBAN"
              value={iban}
            />

            <CopyRow
              label="Account number"
              value={accountNumber}
            />

            <CopyRow
              label="Sort code"
              value={sortCode}
            />

            <CopyRow
              label="Routing number"
              value={routingNumber}
            />

            <CopyRow
              label="BIC / SWIFT"
              value={bicSwift}
            />
          </div>
        )}

        {/* REFERENCE — KEEP HIGH VISIBILITY */}
        {reference && (
          <div className="space-y-2">
            <p className="px-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#0A4730]/35">
              Important
            </p>

            <CopyRow
              label="Reference"
              value={reference}
              important
            />

            <p className="px-2 text-center text-[10px] leading-4 text-[#0A2540]/45">
              Include this reference exactly so your
              payment can be matched to the transfer.
            </p>
          </div>
        )}

        {/* ADDITIONAL DETAILS */}
        {(bankCode ||
          bankAddress ||
          beneficiaryAddress ||
          paymentRails) && (
          <div className="space-y-2">
            <p className="px-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#0A4730]/35">
              Additional bank details
            </p>

            <CopyRow
              label="Bank code"
              value={bankCode}
            />

            <CopyRow
              label="Bank address"
              value={bankAddress}
              multiline
            />

            <CopyRow
              label="Beneficiary address"
              value={beneficiaryAddress}
              multiline
            />

            <CopyRow
              label="Payment rail"
              value={paymentRails}
            />
          </div>
        )}

        <div className="rounded-2xl bg-[#0A4730]/[0.04] px-4 py-3">
          <p className="text-[11px] leading-5 text-[#0A2540]/55">
            Transfer the exact amount shown above.
            Bank processing times can vary.
           </p>
        </div>
      </div>
    </div>
  );
}